/**
 * API client for the ms-agents backend (Flex Comms module).
 *
 * Mirrors src/service/ApiService.js but targets the ms-agents service via
 * msAgentsInstance (REACT_APP_MS_AGENTS_URL). Callers pass full paths such as
 * `get("/api/v1/rooms")` or `post("/api/centrifugo/token")`.
 */
import { msAgentsInstance } from "../../configs/AxiosInstance";

const getAuthConfig = (config) => {
  const token = localStorage.getItem("access_token");
  return {
    headers: {
      ...config.headers,
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
    ...config,
  };
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * ms-agents occasionally returns a transient 401 on the first request of a
 * page load (gateway/backend cold start), which recovers on the very next
 * identical request with no token change involved. Retry once after a short
 * delay before surfacing the error.
 */
const RETRY_DELAY_MS = 600;

export const get = async (endpoint, config = {}) => {
  try {
    const response = await msAgentsInstance.get(endpoint, getAuthConfig(config));
    return response.data;
  } catch (err) {
    if (err.response?.status === 401) {
      await sleep(RETRY_DELAY_MS);
      try {
        const response = await msAgentsInstance.get(endpoint, getAuthConfig(config));
        return response.data;
      } catch (retryErr) {
        console.error("GET request failed after retry:", retryErr);
        throw retryErr;
      }
    }
    console.error("GET request failed:", err);
    throw err;
  }
};

export const post = async (endpoint, data, config = {}) => {
  try {
    const response = await msAgentsInstance.post(endpoint, data, getAuthConfig(config));
    return { data: response.data, status: response.status };
  } catch (err) {
    console.error("POST request failed:", err);
    throw err;
  }
};

export const put = async (endpoint, data, config = {}) => {
  try {
    const response = await msAgentsInstance.put(endpoint, data, getAuthConfig(config));
    return response.data;
  } catch (err) {
    console.error("PUT request failed:", err);
    throw err;
  }
};

export const patch = async (endpoint, data, config = {}) => {
  try {
    const response = await msAgentsInstance.patch(endpoint, data, getAuthConfig(config));
    return response.data;
  } catch (err) {
    console.error("PATCH request failed:", err);
    throw err;
  }
};

export const remove = async (endpoint, config = {}) => {
  try {
    const response = await msAgentsInstance.delete(endpoint, getAuthConfig(config));
    return { data: response.data, status: response.status };
  } catch (err) {
    console.error("DELETE request failed:", err);
    throw err;
  }
};
